import { useEffect, useMemo, useState } from "react";
import { useAvailability } from "@/lib/availability";
import { useCart } from "@/lib/cart";
import { config, placeOrder, sendOrderEmail } from "@/lib/config";
import {
  CATALOG_TABS,
  type CategoryId,
  type ProductPlaceholder,
  type QuantityPrice,
} from "@/data/catalog";
import { useCatalog } from "@/lib/catalog-context";
import "./SmsChat.css";

type View = "shop" | "cart" | "confirmed";

function parseDollars(price: string): number {
  return Number(price.replace(/[^0-9.]/g, "")) || 0;
}

function buildOrderBody(fields: {
  name: string;
  customerNumber: string;
  referralNumber: string;
  area: string;
  lines: string[];
  subtotal: string;
}) {
  const parts: string[] = [];
  if (fields.name) parts.push(`Name: ${fields.name}`);
  parts.push(`Customer #: ${fields.customerNumber}`);
  if (fields.referralNumber) parts.push(`Referral #: ${fields.referralNumber}`);
  parts.push(`Address: ${fields.area}`);
  parts.push("");
  parts.push("Order:");
  parts.push(...fields.lines);
  parts.push("");
  parts.push(`Subtotal: ${fields.subtotal}`);
  return parts.join("\n");
}

export function SmsChat() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>("shop");
  const [cat, setCat] = useState<CategoryId>("flower");
  const [addedSlug, setAddedSlug] = useState<string | null>(null);
  const [selectedFlavors, setSelectedFlavors] = useState<Record<string, string>>({});

  const [name, setName] = useState("");
  const [customerNumber, setCustomerNumber] = useState("");
  const [referralNumber, setReferralNumber] = useState("");
  const [area, setArea] = useState("");

  const { productHasShopStock, isFlavorAvailable } = useAvailability();
  const { flowerSections, placeholders } = useCatalog();

  const catalogProducts = useMemo(
    () => [
      ...flowerSections.flatMap((s) => s.strains),
      ...Object.values(placeholders).flat(),
    ],
    [flowerSections, placeholders],
  );
  const { items, totalItems, addItem, removeItem, setItemQty, clearCart } = useCart();

  function handleAdd(product: ProductPlaceholder, tier: QuantityPrice) {
    const inStock =
      product.flavors?.filter((f) => isFlavorAvailable(product.slug, f)) ?? [];
    const raw = selectedFlavors[product.slug] ?? "";
    const flavor = raw && inStock.includes(raw) ? raw : undefined;
    if (inStock.length > 0 && !flavor) return;
    addItem({
      slug: product.slug,
      name: product.name,
      tier,
      qty: 1,
      imageUrl: product.imageUrl,
      flavor,
    });
    setAddedSlug(product.slug);
    setTimeout(() => setAddedSlug(null), 1200);
  }

  const subtotal = items.reduce((sum, i) => sum + parseDollars(i.tier.price) * i.qty, 0);
  const subtotalStr = `$${subtotal.toFixed(2)}`;
  const canCheckout =
    items.length > 0 && customerNumber.trim().length > 0 && area.trim().length > 0;

  function handleCheckout() {
    const orderLines = items.map((i) => {
      const flavorPart = i.flavor ? ` [${i.flavor}]` : "";
      return `  ${i.qty}x ${i.name}${flavorPart} (${i.tier.qty}) @ ${i.tier.price}`;
    });
    const body = buildOrderBody({
      name: name.trim(),
      customerNumber: customerNumber.trim(),
      referralNumber: referralNumber.trim(),
      area: area.trim(),
      lines: orderLines,
      subtotal: subtotalStr,
    });
    sendOrderEmail(body, customerNumber.trim());
    placeOrder(body, customerNumber.trim());
    clearCart();
    setName("");
    setCustomerNumber("");
    setReferralNumber("");
    setArea("");
    setView("confirmed");
  }

  const inMenu = (p: ProductPlaceholder) => productHasShopStock(p.slug, p.flavors);

  const products: { section?: string; product: ProductPlaceholder }[] =
    cat === "flower"
      ? flowerSections.flatMap((s) => s.strains.map((p) => ({ section: s.title, product: p })))
      : (placeholders[cat] ?? []).map((p) => ({ product: p }));

  useEffect(() => {
    setSelectedFlavors((prev) => {
      const bySlug = new Map(catalogProducts.map((p) => [p.slug, p]));
      const next = { ...prev };
      let changed = false;
      for (const [slug, flavor] of Object.entries(prev)) {
        if (!flavor) continue;
        const p = bySlug.get(slug);
        if (!p || !isFlavorAvailable(p.slug, flavor)) {
          delete next[slug];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [catalogProducts, isFlavorAvailable]);

  return (
    <>
      <button
        type="button"
        className={`sms-fab ${open ? "sms-fab--open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close shop" : "Quick order"}
      >
        {open ? (
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          >
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        ) : (
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="9" cy="21" r="1" />
            <circle cx="20" cy="21" r="1" />
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
          </svg>
        )}
      </button>

      {open && (
        <div className="shop-panel" role="dialog" aria-label="Quick order">
          <header className="shop-panel__header">
            <button
              type="button"
              className={`shop-panel__tab ${view === "shop" ? "shop-panel__tab--active" : ""}`}
              onClick={() => setView("shop")}
            >
              Menu
            </button>
            <button
              type="button"
              className={`shop-panel__tab ${view === "cart" ? "shop-panel__tab--active" : ""}`}
              onClick={() => setView("cart")}
            >
              Cart{totalItems > 0 && <span className="shop-panel__badge">{totalItems}</span>}
            </button>
          </header>

          {view === "shop" && (
            <>
              <nav className="shop-panel__cats">
                {CATALOG_TABS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`shop-panel__cat ${cat === t.id ? "shop-panel__cat--active" : ""}`}
                    onClick={() => setCat(t.id)}
                  >
                    {t.label}
                  </button>
                ))}
              </nav>

              <div className="shop-panel__products">
                {products.map(({ section, product }) => (
                  <div key={product.slug} className="shop-product">
                    <div className="shop-product__head">
                      {product.imageUrl && (
                        <img
                          src={product.imageUrl}
                          alt=""
                          className="shop-product__img"
                          loading="lazy"
                          decoding="async"
                        />
                      )}
                      <div className="shop-product__info">
                        <span className="shop-product__name">{product.name}</span>
                        {section && <span className="shop-product__section">{section}</span>}
                      </div>
                      {addedSlug === product.slug && (
                        <span className="shop-product__added">Added</span>
                      )}
                    </div>
                    {inMenu(product) ? (() => {
                      const inStock =
                        product.flavors?.filter((f) =>
                          isFlavorAvailable(product.slug, f),
                        ) ?? [];
                      const raw = selectedFlavors[product.slug] ?? "";
                      const flavorValue =
                        raw && inStock.includes(raw) ? raw : "";
                      const needsFlavorPick = inStock.length > 0 && !flavorValue;
                      return (
                        <>
                          {inStock.length > 0 && (
                            <select
                              className="shop-product__flavor"
                              value={flavorValue}
                              onChange={(e) =>
                                setSelectedFlavors((prev) => ({
                                  ...prev,
                                  [product.slug]: e.target.value,
                                }))
                              }
                            >
                              <option value="" disabled>
                                Select flavor *
                              </option>
                              {inStock.map((f) => (
                                <option key={f} value={f}>
                                  {f}
                                </option>
                              ))}
                            </select>
                          )}
                          {needsFlavorPick ? (
                            <span className="shop-product__flavor-hint">
                              Choose a flavor to add
                            </span>
                          ) : (
                            <div className="shop-product__tiers">
                              {product.tiers.map((tier) => (
                                <button
                                  key={tier.qty}
                                  type="button"
                                  className="shop-tier"
                                  onClick={() => handleAdd(product, tier)}
                                >
                                  <span className="shop-tier__qty">{tier.qty}</span>
                                  <span className="shop-tier__price">{tier.price}</span>
                                  <span className="shop-tier__plus">+</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </>
                      );
                    })() : (
                      <span className="shop-product__sold-out">Out of Stock</span>
                    )}
                  </div>
                ))}
              </div>

              {totalItems > 0 && (
                <button
                  type="button"
                  className="shop-panel__view-cart"
                  onClick={() => setView("cart")}
                >
                  View Cart &middot; {totalItems} item{totalItems !== 1 ? "s" : ""} &middot;{" "}
                  {subtotalStr}
                </button>
              )}
            </>
          )}

          {view === "confirmed" && (
            <div className="shop-confirmed">
              <div className="shop-confirmed__icon" aria-hidden="true">
                &#x2714;
              </div>
              <h3 className="shop-confirmed__title">To Complete Your Order</h3>
              <p className="shop-confirmed__text">
                Send the Order Detail in your messaging app. We&rsquo;ll confirm shortly.
              </p>
              <p className="shop-confirmed__phone">
                <a href={`tel:${config.contactPhoneTel}`}>{config.contactPhoneDisplay}</a>
              </p>
              <button type="button" className="shop-confirmed__btn" onClick={() => setView("shop")}>
                Back to Menu
              </button>
            </div>
          )}

          {view === "cart" && (
            <div className="shop-cart">
              {items.length === 0 ? (
                <div className="shop-cart__empty">
                  <p>Your cart is empty.</p>
                  <button
                    type="button"
                    className="shop-cart__browse"
                    onClick={() => setView("shop")}
                  >
                    Browse menu
                  </button>
                </div>
              ) : (
                <>
                  <ul className="shop-cart__items">
                    {items.map((item) => (
                      <li
                        key={`${item.slug}-${item.tier.qty}-${item.flavor ?? ""}`}
                        className="shop-cart-item"
                      >
                        <div className="shop-cart-item__info">
                          <span className="shop-cart-item__name">{item.name}</span>
                          <span className="shop-cart-item__tier">
                            {item.tier.qty} &mdash; {item.tier.price}
                          </span>
                          {item.flavor && (
                            <span className="shop-cart-item__flavor">{item.flavor}</span>
                          )}
                        </div>
                        <div className="shop-cart-item__controls">
                          <button
                            type="button"
                            className="shop-cart-item__btn"
                            aria-label="Decrease"
                            onClick={() =>
                              setItemQty(item.slug, item.tier.qty, item.qty - 1, item.flavor)
                            }
                          >
                            &minus;
                          </button>
                          <span className="shop-cart-item__qty">{item.qty}</span>
                          <button
                            type="button"
                            className="shop-cart-item__btn"
                            aria-label="Increase"
                            onClick={() =>
                              setItemQty(item.slug, item.tier.qty, item.qty + 1, item.flavor)
                            }
                          >
                            +
                          </button>
                        </div>
                        <button
                          type="button"
                          className="shop-cart-item__remove"
                          aria-label={`Remove ${item.name}`}
                          onClick={() => removeItem(item.slug, item.tier.qty, item.flavor)}
                        >
                          &times;
                        </button>
                      </li>
                    ))}
                  </ul>

                  <div className="shop-cart__fields">
                    <input
                      type="text"
                      className="shop-cart__input"
                      placeholder="Name (optional)"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                    <input
                      type="text"
                      className="shop-cart__input shop-cart__input--req"
                      placeholder="Customer # *"
                      value={customerNumber}
                      onChange={(e) => setCustomerNumber(e.target.value)}
                      required
                    />
                    <input
                      type="text"
                      className="shop-cart__input"
                      placeholder="Referral # (if applicable)"
                      value={referralNumber}
                      onChange={(e) => setReferralNumber(e.target.value)}
                    />
                    <input
                      type="text"
                      className="shop-cart__input shop-cart__input--req"
                      placeholder="Address / Cross Street *"
                      value={area}
                      onChange={(e) => setArea(e.target.value)}
                      required
                    />
                  </div>

                  <div className="shop-cart__footer">
                    <div className="shop-cart__total">
                      <span>
                        {totalItems} item{totalItems !== 1 ? "s" : ""}
                      </span>
                      <strong>{subtotalStr}</strong>
                    </div>
                    {canCheckout ? (
                      <button
                        type="button"
                        className="shop-cart__checkout"
                        onClick={handleCheckout}
                      >
                        Place Order
                      </button>
                    ) : (
                      <span className="shop-cart__checkout shop-cart__checkout--disabled">
                        Fill required fields to order
                      </span>
                    )}
                    <button type="button" className="shop-cart__clear" onClick={clearCart}>
                      Clear cart
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
